import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { Loader } from "semantic-ui-react";

import { DATE_FORMATS_FNS } from "components";
import { useScrollPageToTop } from "hooks";
import { createFormHelper } from "lib/Form";
import { useBrandGroupsQuery } from "modules/BrandsDashboard/hooks";
import {
  AiTablet,
  CustomerCommunication,
  DmsSystem,
  General,
  Integrations,
  KeylabelKeylocker,
  LocationDetailActions,
  LocationDetailHeader,
  Service,
  Tyres,
  Workorder,
  buildTimeslotDeadline,
  isKeylockerCodeInRange
} from "modules/DealerLocationDetails/components";
import "modules/DealerLocationDetails/DealerLocationDetails.scss";
import { useLocationDetailsQuery, useUpdateLocationMutation } from "modules/DealerLocationDetails/hooks";
import { useDealersQuery } from "modules/DealersDashboard/components/DealersDashboardTable/hooks";
import { areIpAddressesValid, parseIpAddresses } from "modules/LocationOverviewDashboard/utils";
import { NotFoundPage } from "router/components";
import { RESOURCE_NOT_FOUND } from "utils/Api";
import { formatCurrentUTCDate } from "utils/dateHelperFunctions";

import type { AAEquipmentTyreScanner, BrandGroupID, Dealer, DealerLocation, Timeslot } from "models";

export type LocationAdminFormPayload = Omit<
  Pick<
    DealerLocation,
    | "active"
    | "track_dms_importing"
    | "name"
    | "commercial_name"
    | "headline"
    | "footerline"
    | "country"
    | "city"
    | "street"
    | "postal_code"
    | "phone_nr"
    | "language_code"
    | "logo"
    | "company_stamp"
    | "brand_group_ids"
    | "importer_version"
    | "bar_code_type"
    | "ip_address_enabled"
    | "ip_addresses"
    | "dbb_enabled"
    | "dbb_location_id"
    | "dbb_user"
    | "is_employee_name_visible"
    | "redirect_to_new_web_version"
    | "convert_checklist_to_intervention_is_enabled"
    | "manually_create_intervention_is_enabled"
    | "create_wo_is_enabled"
    | "can_edit_checklist_after_car_ready"
    | "accept_network_metrics"
    | "date_visible"
    | "apk_visible"
    | "hu_visible"
    | "is_customer_answered_checkin_mandatory"
    | "is_three_departments_enabled"
    | "default_customer"
    | "car_ready_btn_visible"
    | "dp_driver_visible"
    | "dp_contractor_visible"
    | "dp_owner_visible"
    | "service_box_visible_on_car"
    | "service_box_visible_on_wo"
    | "dms_price_enabled"
    | "dms_billing_enabled"
    | "include_vat"
    | "vat"
    | "appointment_detail_users_indicator_enabled"
    | "is_car_in_shop_print_enabled"
    | "is_editing_question_videos_enabled"
    | "is_next_km_and_next_date_optional"
    | "hide_next_km_and_next_date"
    | "default_customer_ok_for_dms_lease_intervention"
    | "is_car_note_enabled"
    | "is_customer_note_enabled"
    | "is_assigned_wo_status_change_notification_enabled"
    | "is_tablet_translation_ai_enabled"
    | "auto_unpin_appointment_statuses"
    | "unpin_past_appointments_older_than"
    | "unpin_future_appointments_further_than"
    | "unpin_appointments_on_is_billed"
    | "unpin_appointments_on_car_out_of_shop"
    | "automatically_pin_appointments"
    | "automatically_pin_car_in_shop"
    | "is_manually_pinning_appointments_enabled"
    | "is_automatically_pinned_appointments_readonly"
    | "enabled_appointment_note_type_ids"
    | "first_name_optional"
    | "recurring_car_duration"
    | "timeslots"
    | "key_label_type_id"
    | "key_label_layout_id"
    | "keylocker_code"
    | "dms_location_source"
    | "can_add_intervention_elements"
    | "video_enabled"
    | "video_capture_length_in_seconds"
    | "schedule_enabled"
    | "planit_planning_establishment_id"
    | "planit_planning_replacement_vehicle"
    | "web_clock_omgeving_id"
    | "xentry_sub_outlet_id"
    | "is_dayplanner_enabled"
    | "automatically_assign_wo_when_car_check_started"
    | "automatically_assign_wo_when_intervention_fixed"
    | "third_party_send_communications"
    | "online_check_in_enabled"
    | "desk_check_in_enabled"
    | "is_open_desk_checkout_on_car_out_of_shop_enabled"
    | "car_out_of_shop_at_desk_checkout_enabled"
    | "diagnose_overview_enabled"
    | "repair_overview_enabled"
    | "is_keylocker_enabled"
    | "is_kiosk_enabled"
    | "all_season_a_threshold"
    | "all_season_n_threshold"
    | "summer_a_threshold"
    | "summer_n_threshold"
    | "winter_a_threshold"
    | "winter_n_threshold"
    | "truck_tyre_a_threshold"
    | "truck_tyre_n_threshold"
    | "is_tyre_team_enabled"
    | "is_tyre_team_autofill_price_ref_on_order"
    | "is_tyre_net_enabled"
    | "is_tyre_motion_enabled"
    | "is_tire_scanner_enabled"
    | "is_aaequipment_enabled"
    | "aa_equipment_tyre_scanners"
    | "is_sharebox_enabled"
    | "sharebox_api_client_id"
    | "sharebox_customer_id"
    | "sharebox_session_id"
    | "evhc_export_enabled"
    | "evhc_primary_code"
    | "evhc_secondary_code"
    | "is_pon_enabled"
    | "pon_location_number"
    | "is_robnet_enabled"
    | "robnet_location_number"
    | "is_acses_enabled"
    | "acses_email"
    | "upgrade_car_with_rdw"
    | "upgrade_with_rdw_apk_date"
    | "write_back_rdw_apk_date"
    | "is_recalls_service_enabled"
    | "is_lef_enabled"
    | "lef_account_number"
    | "pin_visible"
    | "is_warranty_pin_signature_missing_warning_enabled"
    | "is_warranty_pin_support_warning_enabled"
    | "is_warranty_pin_claim_warning_enabled"
    | "is_recall_pin_support_warning_enabled"
    | "is_recall_pin_claim_warning_enabled"
    | "generate_ai_summary"
    | "is_exact_invoicing_enabled"
  >,
  "ip_addresses"
> & {
  ip_addresses: string;
  warranty_pin_signature_brand_group_ids: BrandGroupID[];
  keyloop_business_unit: string;
  dbb_password?: string;
  sharebox_api_client_secret?: string;
  sharebox_weblink_access_key?: string;
  sharebox_private_key?: string;
  robnet_api_key?: string;
  acses_password?: string;
  tyre_net_tic_uid?: string;
  tyre_net_tic_id?: string;
};

const FormHelper = createFormHelper<LocationAdminFormPayload>();

const DEFAULT_TIMESLOT_TIMES = ["12:00", "13:00", "14:00", "15:00"];

const getFormTimeslots = (location: DealerLocation): Timeslot[] =>
  DEFAULT_TIMESLOT_TIMES.map(
    (time, idx) =>
      location.timeslots?.[idx] ?? {
        created_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
        updated_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
        id: 0,
        deadline: buildTimeslotDeadline(time),
        active: false,
        dealer_location_id: location.id
      }
  );

const NUMBER_OF_CONFIGURABLE_SCANNERS = 5;

const getFormTyreScanners = (location: DealerLocation): AAEquipmentTyreScanner[] =>
  Array.from({ length: NUMBER_OF_CONFIGURABLE_SCANNERS }, (_, idx) => {
    const shortCode = idx + 1;

    return (
      location.aa_equipment_tyre_scanners?.find(scanner => scanner.short_code === shortCode) ?? {
        created_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
        updated_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
        short_code: shortCode,
        serial_number: "",
        last_fr_profile_scanned: 0,
        last_fl_profile_scanned: 0,
        last_rr_profile_scanned: 0,
        last_rl_profile_scanned: 0,
        last_alignment_recommended_scanned: false,
        last_report_scanned: "",
        dealer_location_id: location.id
      }
    );
  });

const getKeyloopBusinessUnit = (location: DealerLocation) =>
  "keyloop_business_unit" in location && typeof location.keyloop_business_unit === "string" ? location.keyloop_business_unit : "";

const getDefaultFormValues = (location: DealerLocation): LocationAdminFormPayload => ({
  ...location,
  ip_addresses: location.ip_address_enabled ? (location.ip_addresses?.join("\n") ?? "") : "",
  brand_group_ids: location.brand_group_ids ?? [],
  auto_unpin_appointment_statuses: location.auto_unpin_appointment_statuses ?? [],
  enabled_appointment_note_type_ids: location.enabled_appointment_note_type_ids ?? [],
  warranty_pin_signature_brand_group_ids: location.warranty_pin_signature_brand_groups?.map(brandGroup => brandGroup.id) ?? [],
  timeslots: getFormTimeslots(location),
  aa_equipment_tyre_scanners: getFormTyreScanners(location),
  keyloop_business_unit: getKeyloopBusinessUnit(location)
});

const clearDisabledServiceSecrets = (values: LocationAdminFormPayload, dealer?: Dealer): LocationAdminFormPayload => ({
  ...values,
  ...(!values.is_robnet_enabled && { robnet_api_key: undefined }),
  ...(!values.is_sharebox_enabled && { sharebox_api_client_secret: undefined, sharebox_weblink_access_key: undefined, sharebox_private_key: undefined }),
  ...(!values.is_dayplanner_enabled && { automatically_assign_wo_when_car_check_started: false, automatically_assign_wo_when_intervention_fixed: false }),
  ...(!values.is_acses_enabled && { acses_email: "", acses_password: undefined }),
  ...(dealer && !dealer.dbb_enabled && { dbb_enabled: false, dbb_user: "", dbb_password: undefined })
});

const isFormInvalid = (values: LocationAdminFormPayload, location: DealerLocation) => {
  const areRequiredFieldsValid = Boolean(values.name && values.commercial_name && values.country && values.language_code);
  const isKeylockerCodeValid = isKeylockerCodeInRange(values.keylocker_code);
  const areUnpinNumbersValid = values.unpin_past_appointments_older_than >= 0 && values.unpin_future_appointments_further_than >= 0;
  const areIpAddressesEntered = !values.ip_address_enabled || areIpAddressesValid(values.ip_addresses);
  const areThresholdsValid =
    values.all_season_n_threshold <= values.all_season_a_threshold &&
    values.summer_n_threshold <= values.summer_a_threshold &&
    values.winter_n_threshold <= values.winter_a_threshold &&
    values.truck_tyre_n_threshold <= values.truck_tyre_a_threshold;

  const isShareboxValid = !values.is_sharebox_enabled || Boolean(values.sharebox_api_client_id && values.sharebox_customer_id && values.sharebox_session_id);
  const isEvhcValid = !values.evhc_export_enabled || Boolean(values.evhc_primary_code && values.evhc_secondary_code);
  const isAcsesValid = !values.is_acses_enabled || Boolean(values.acses_email && (location.has_acses_password || values.acses_password));
  const isTyreNetValid =
    !values.is_tyre_net_enabled || Boolean((location.has_tyre_net_tic_uid || values.tyre_net_tic_uid) && (location.has_tyre_net_tic_id || values.tyre_net_tic_id));

  return !(
    areRequiredFieldsValid &&
    isKeylockerCodeValid &&
    areUnpinNumbersValid &&
    areIpAddressesEntered &&
    areThresholdsValid &&
    isShareboxValid &&
    isEvhcValid &&
    isAcsesValid &&
    isTyreNetValid
  );
};

export const DealerLocationDetails = () => {
  const locationId = Number(useParams<{ locationId: string }>().locationId);

  const { data: location, isLoading: isLocationDetailsLoading, error: locationDetailsError } = useLocationDetailsQuery(locationId);
  const { data: dealers } = useDealersQuery();
  const { data: brandGroups = [] } = useBrandGroupsQuery();
  const { mutate: updateLocation, isPending: isUpdateLocationPending } = useUpdateLocationMutation();
  const defaultValues = useMemo(() => location && getDefaultFormValues(location), [location]);
  useScrollPageToTop();

  if (isLocationDetailsLoading) return <Loader active />;
  if (locationDetailsError?.message.includes(RESOURCE_NOT_FOUND)) return <NotFoundPage />;
  if (!location) return null;

  const dealer = dealers?.find(({ id }) => id === location.dealer_id);

  const onSubmit = (values: LocationAdminFormPayload) => {
    if (isFormInvalid(values, location)) return;

    const { ip_addresses, aa_equipment_tyre_scanners, warranty_pin_signature_brand_group_ids, ...locationValues } = clearDisabledServiceSecrets(values, dealer);

    updateLocation({
      ...location,
      ...locationValues,
      ip_addresses: parseIpAddresses(ip_addresses),
      aa_equipment_tyre_scanners: aa_equipment_tyre_scanners?.filter(scanner => scanner.serial_number),
      warranty_pin_signature_brand_groups: warranty_pin_signature_brand_group_ids.flatMap(
        brandGroupId => brandGroups.find(brandGroup => brandGroup.id === brandGroupId) ?? []
      )
    });
  };

  return (
    <FormHelper.Form onSubmit={onSubmit} defaultValues={defaultValues}>
      <div className="DealerLocationDetails">
        <LocationDetailHeader isUpdateLocationPending={isUpdateLocationPending} />
        <General tabId="general" location={location} dealer={dealer} />
        <Workorder tabId="workorder" />
        <KeylabelKeylocker tabId="keylabelKeylocker" />
        <DmsSystem tabId="dmsSystem" location={location} dealer={dealer} />
        <CustomerCommunication tabId="customerCommunication" />
        <Service tabId="service" location={location} dealer={dealer} />
        <Tyres tabId="tyres" location={location} />
        <Integrations tabId="integrations" location={location} />
        <AiTablet tabId="aiTablet" location={location} />
        <LocationDetailActions location={location} />
      </div>
    </FormHelper.Form>
  );
};
